import { Container } from "../components/common-components";
import { FormHeader } from "../components/FormHeader";
import { FormFooter } from "../components/FormFooter";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import html2pdf from "html2pdf.js";
import { ReactSVG } from "react-svg";
import { fetchNotepad, sendEmailWithPdf, uploadPdf } from "../../../redux/modules/patient/actions";
import { getPharmacyInfo } from "../common/getPharmacyInfo";
import { displayToast } from "../../../redux/modules/toast/actions";
import ConfirmationModalNew from "../../common/ConfirmationModalNew";
import NotesList from "../components/NotesList";
import { getNoteDateForFilter } from "../../../helpers/functions";

const PatientChartForm = ({ patient }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id: patientId, summaryId } = useParams();
  const { notepad, formsNotepad } = useSelector((state) => state.patients || {});
  const { currentLoggedInUserDetail: userFromCurrent } = useSelector((state) => state.currentLoggedInUserDetail ?? {});
  const { currentLoggedInUserDetail: userFromUsers, pharmacyStores } = useSelector((state) => state.users ?? {});
  const pharmacy = getPharmacyInfo(userFromCurrent ?? userFromUsers, pharmacyStores) ?? {};
  const navigate = useNavigate();
  const printableRef = useRef(null);
  const printButtonRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (patientId) dispatch(fetchNotepad(patientId));
  }, [patientId, dispatch]);

  const source = (formsNotepad && formsNotepad.length > 0) ? formsNotepad : (notepad || []);

  const params = new URLSearchParams(location.search);
  const serviceDate = params.get('serviceDate');

  let notesToShow = source;
  if (serviceDate) {
    const start = new Date(serviceDate + 'T00:00:00Z');
    const end = new Date(serviceDate + 'T23:59:59.999Z');
    notesToShow = source.filter(n => {
      const d = getNoteDateForFilter(n);
      if (!d) return false;
      return d >= start && d <= end;
    });
  }

  const selectedRowId = params.get('selectedRowId');
  if (selectedRowId) {
    const found = notesToShow.find(n => n.id === selectedRowId);
    if (found) notesToShow = [found];
  }

  const firstNote = notesToShow?.[0];
  const startDateTime = firstNote
    ? (firstNote.systemPeriod
        ? (typeof firstNote.systemPeriod === 'string'
            ? firstNote.systemPeriod
            : firstNote.systemPeriod.lowerBound || firstNote.systemPeriod.LowerBound)
        : (firstNote.date || firstNote.Date || firstNote.createdAt || firstNote.CreatedAt))
    : null;

  function handlePrint() {
    if (!printableRef.current) {
      window.print();
      return;
    }

    const html = printableRef.current.innerHTML;
    const newWin = window.open("", "_blank");
    if (!newWin) {
      window.print();
      return;
    }

    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map((l) => l.outerHTML)
      .join("\n");
    const styles = Array.from(document.querySelectorAll("style"))
      .map((s) => s.outerHTML)
      .join("\n");

    newWin.document.open();
    newWin.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Print</title>${links}${styles}<style>@page{size: letter portrait; margin: 0.5in;} body{margin:0; padding:0; -webkit-print-color-adjust:exact; print-color-adjust:exact;} .printable-area{width:7.5in; margin:0 auto;} .print-section{break-inside:avoid-page; page-break-inside:avoid;} .print-section + .print-section{break-before:page; page-break-before:always;} .printable-area *{max-height:none !important;} </style></head><body>`
    );
    newWin.document.write(`<div class="print-container printable-area">${html}</div>`);
    newWin.document.write("</body></html>");
    newWin.document.close();
    newWin.focus();
    setTimeout(() => {
      try {
        newWin.print();
      } catch (err) {
        console.error("Print failed", err);
      }
      try {
        newWin.close();
      } catch (e) {
        /* ignore */
      }
    }, 500);
  }

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  async function handleSendEmail() {
    const normalizedEmail = (email || "").trim();
    if (!normalizedEmail) {
      setEmailError("Please enter recipient email");
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!printableRef.current) {
      setEmailError("Document is not ready to send yet");
      return;
    }

    const formDisplayName = "Patient Chart - Pharmacist Comments";

    try {
      setLoading(true);
      setEmailError("");

      const pdfBlob = await html2pdf()
        .set({
          margin: 0,
          filename: `${formDisplayName.replace(/\s+/g, "-")}.pdf`,
          image: { type: "jpeg", quality: 1 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: {
            unit: "in",
            format: "letter",
            orientation: "portrait",
          },
        })
        .from(printableRef.current)
        .outputPdf("blob");

      const pdfFilename = `${formDisplayName.replace(/\s+/g, "-")}.pdf`;
      const pdfLink = await dispatch(uploadPdf(pdfBlob, pdfFilename));

      const formData = new FormData();
      formData.append("File", pdfBlob, pdfFilename);
      formData.append("ToEmail", normalizedEmail);
      formData.append("Subject", formDisplayName);
      formData.append("PatientName", `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim());
      formData.append("FormName", formDisplayName);
      formData.append("PharmacyName", pharmacy.pharmacyName || "");
      formData.append("StoreNumber", pharmacy.pharmacyStoreNumber || "");
      formData.append("PhoneNumber", pharmacy.pharmacyPhone || "");
      formData.append("FaxNumber", pharmacy.pharmacyFax || "");
      formData.append("StoreEmail", pharmacy.pharmacyEmail || "");
      formData.append("PdfLink", pdfLink);

      await dispatch(sendEmailWithPdf(formData));
      dispatch(displayToast("Email sent successfully", "success", { autoClose: 4000 }));
      setShowEmailModal(false);
      setEmail("");
    } catch (error) {
      setEmailError(error?.message || "Failed to send email");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <div className={`p-9 flex flex-row w-full justify-between items-start gap-6`}>
      <div className={`xl:min-w-[180px] print:hidden`}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="flex items-center justify-center"
        >
          <ReactSVG
            src="/icons/back-arrow.svg"
            beforeInjection={(svg) => {
              svg.setAttribute("class", "w-16 h-16");
            }}
          />
        </button>
      </div>

      <div className="relative">
        <div className={`overflow-hidden w-[8.5in] min-h-[11in]`}> 
          <div ref={printableRef} className="space-y-20 mx-auto">
            <div className="print-section">
            <Container>
              <>
                <FormHeader cover={false} formType={"patientChart"} section="pharmacist" />
                <NotesList
                  notepad={notesToShow}
                  clinicalServiceName={null}
                  patient={patient}
                  startDateTime={startDateTime}
                  serviceId={null}
                  summaryId={summaryId}
                  commentType="pharmacist"
                  showOnlyPatientChart={true}
                />
                <FormFooter />
              </>
            </Container>
            </div>

            <div className="print-section">
            <Container>
              <>
                <FormHeader cover={false} formType={"patientChart"} section="patient" />
                <NotesList
                  notepad={notesToShow}
                  clinicalServiceName={null}
                  patient={patient}
                  startDateTime={startDateTime}
                  serviceId={null}
                  summaryId={summaryId}
                  commentType="patient"
                  showOnlyPatientChart={true}
                />
                <FormFooter />
              </>
            </Container>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row bg-white print:hidden">
        <button
          type="button"
          aria-label="Print"
          ref={printButtonRef}
          onClick={() => handlePrint()}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/printer-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute("class", "w-9 h-9");
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4 z-10">
            Print
          </span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button
          type="button"
          aria-label="Email"
          onClick={() => {
            setEmailError("");
            setShowEmailModal(true);
          }}
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/mail-open.svg"
            beforeInjection={(svg) => {
              svg.setAttribute("class", "w-9 h-9");
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-20 text-gray-800 font-semibold shadow rounded py-2 px-4 z-10">
            Email
          </span>
        </button>

        <div className="hidden xl:block w-px bg-gray-100 self-center h-9" />
        <button
          type="button"
          aria-label="Fax"
          className="flex items-center justify-center relative group p-2 rounded hover:bg-gray-20"
        >
          <ReactSVG
            src="/icons/fax-outline.svg"
            beforeInjection={(svg) => {
              svg.setAttribute("class", "w-9 h-9");
            }}
          />
          <span className="pointer-events-none absolute top-full mt-3 left-1/2 -translate-x-[85%] hidden group-hover:block bg-gray-20 text-gray-800 shadow rounded py-2 px-4 whitespace-nowrap z-10">
            <span className="font-semibold">Email Fax</span> - Coming Soon
          </span>
        </button>
      </div>
    </div>
    <ConfirmationModalNew
      open={showEmailModal}
      title={"Send Email"}
      className={"!w-[400px] !max-w-[400px] !p-0"}
      content={() => (
        <div className="p-2">
          <input
            type="email"
            placeholder="Enter recipient email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            className="w-full border border-gray-300 p-2 rounded mb-2"
          />
          {emailError && <p className="text-sm text-red-600 mb-3">{emailError}</p>}
        </div>
      )}
      handleClose={() => {
        setShowEmailModal(false);
        setEmailError("");
      }}
      onCross={() => {
        setShowEmailModal(false);
        setEmailError("");
      }}
      confirmText={loading ? "Sending..." : "Send"}
      cancelText={"Cancel"}
      confirmAction={handleSendEmail}
      cancelAction={() => {
        setShowEmailModal(false);
        setEmailError("");
      }}
      disableConfirm={loading}
      hideCloseIcon={false}
    />
    </>
  );
};

export default PatientChartForm;
